import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { TravelPolicyQuotationFormValue } from "../travel-policy/_interfaces/travel-policy.interface";
import { Result } from "../../../shared/interfaces/common.interface";
import {
    PolicyDetailsGroup,
    PolicyListing,
    PremiumModel,
    SubscriptionScenario,
    PaymentCallbackDetails
} from "../_interfaces/policies.interface";
import { getPolicyLabel, PolicyEnum, PolicyStatusEnum } from "../../coverages/_constants";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { getTravelDestinationLabel } from "../travel-policy/_constants/travel-policy.constant";

@Injectable({ providedIn: "root" })
export class PoliciesService {
    // TODO (Refactor start): Move this code to a separate file
    steps: number[] = [0];
    private basePath = `${environment.url}/policy`;
    private paymentPath = `${environment.url}/payment`;

    constructor(private http: HttpClient) { }

    addStep(index: number): void {
        const existing = this.steps.findIndex((step) => step === index);
        if (existing !== -1) {
            return;
        }

        this.steps.push(index);
    }

    removeStep(index: number): void {
        const existing = this.steps.findIndex((step) => step === index);
        if (existing === -1) {
            return;
        }

        this.steps.splice(existing, 1);
    }
    // TODO (Refactor end)

    calculatePremium$(formValue: TravelPolicyQuotationFormValue): Observable<PremiumModel> {
        const daysBetween = formValue.returnDate!.diff(formValue.departureDate, "days") + 1; // +1 to include the departure date

        return this.http
            .get<Result<PremiumModel>>(`${this.basePath}/calculatePremium`, {
                params: {
                    policyType: PolicyEnum.TravelProtectPlus,
                    days: daysBetween,
                    area: formValue.destination!,
                },
            })
            .pipe(map((res) => res.data));
    }

    submitPolicy$(
        productType: PolicyEnum,
        email: string,
        formValue?: TravelPolicyQuotationFormValue,
    ): Observable<string> {
        const payload: Record<string, any> = {
            productType,
            email,
        };

        if (formValue) {
            payload["startDate"] = formValue.departureDate!.toDate();
            payload["endDate"] = formValue.returnDate!.toDate();
            payload["area"] = formValue.destination;
        }

        return this.http.post<Result<string>>(`${this.basePath}/submitPolicy`, payload).pipe(map((res) => res.data));
    }

    getPolicyListing$(): Observable<PolicyListing[]> {
        return this.http.get<Result<PolicyListing[]>>(`${this.basePath}/getPolicyListing`).pipe(
            map((res) => res.data),
            map((data) =>
                data.map((policy) => ({
                    ...policy,
                    UI_isActive: policy.status === PolicyStatusEnum.Active,
                    UI_productType: getPolicyLabel(policy.productType),
                })),
            ),
        );
    }

    getPolicyDetails$(id: string): Observable<PolicyDetailsGroup> {
        return this.http.get<Result<PolicyDetailsGroup>>(`${this.basePath}/getPolicyDetails/${id}`).pipe(
            map((res) => res.data),
            map((details) => ({
                ...details,
                policyDetails: {
                    ...details.policyDetails,
                    UI_area: getTravelDestinationLabel(details.policyDetails.area),
                    UI_isActive: details.policyDetails.status === PolicyStatusEnum.Active,
                    UI_productType: getPolicyLabel(details.policyDetails.productType),
                },
            })),
        );
    }

    getDocument$(documentId: string): Observable<Blob> {
        return this.http.get(`${this.basePath}/getDocument/${documentId}`, {
            responseType: "blob",
        });
    }

    subscriptionScenario$(productType: PolicyEnum): Observable<SubscriptionScenario> {
        return this.http
            .get<Result<SubscriptionScenario>>(`${this.basePath}/subscriptionScenario?productType=${productType}`)
            .pipe(map((res) => res.data));
    }

    unsubscribe$(policyId: string): Observable<null> {
        return this.http.post<Result<null>>(`${this.basePath}/unsubscribe`, { policyId }).pipe(map((res) => res.data));
    }

    getPaymentCallbackDetails$(
        clientRefNum: string,
        fullUrl: string,
    ): Observable<PaymentCallbackDetails> {
        const payload: Record<string, any> = {
            clientRefNum,
            fullUrl,
        };

        return this.http.post<Result<PaymentCallbackDetails>>(`${this.paymentPath}/getPaymentCallbackDetails`, payload).pipe(map(res => res.data));
    }
}
